import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
// local
import { AuthStatusTypes } from 'src/app/core/models/auth-status-types';
import { InsightsService } from 'src/app/core/services/insights/insights.service';

// third party
import { OverlayContainer } from '@angular/cdk/overlay';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
    MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService
} from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
import config from "devextreme/core/config";
import { licenseKey } from 'src/devextreme-license'; 
 
config({
  licenseKey
});
 
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'tbtu-app';
  authStatus: AuthStatusTypes = AuthStatusTypes.LOADING;
  nonProd = !environment.production;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private insights: InsightsService,
    overlayContainer: OverlayContainer,
  ) {
    // Menus and dialogs render outside the app root, so they need the theme class too.
    overlayContainer.getContainerElement().classList.add('light-theme');
  }

  ngOnInit(): void {
    this.insights.appInsights.trackTrace({ message: 'Track me' });
    this.insights.appInsights.trackEvent({ name: 'Tracking event...' });

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe(() => {
        this.setAuthStatus();
      });

    this.msalBroadcastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None), takeUntil(this._destroying$))
      .subscribe(() => {
        this.setAuthStatus();
        this.checkAndSetActiveAccount();
      });
  }

  setAuthStatus() {
    this.authStatus = AuthStatusTypes.LOADING;
    if (this.authService.instance.getAllAccounts().length > 0) {
      this.authStatus = AuthStatusTypes.AUTHENTICATED;
    } else {
      this.authStatus = AuthStatusTypes.UNAUTHENTICATED;
    }
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  login() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  get isLoading(): boolean {
    return this.authStatus === AuthStatusTypes.LOADING;
  }

  get isAuthenticated(): boolean {
    return this.authStatus === AuthStatusTypes.AUTHENTICATED;
  }

  get isUnAuthenticated(): boolean {
    return this.authStatus === AuthStatusTypes.UNAUTHENTICATED;
  }
}